import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import FileDownload from 'js-file-download';
import axios from 'axios';

import {
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Modal,
  Box,
  Typography,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { get, isNaN, isNull, omitBy } from 'lodash';
import { sentenceCase } from 'change-case';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import { api as stockApi, hooks as stockHooks } from '../store/domains/stock';
import { BASE_URL } from '../constants';

import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { headers } from '../services';

const MySwal = withReactContent(Swal);

const TABLE_HEAD = [
  { id: 'info', label: 'Info', alignRight: false },
  { id: 'dealerCode', label: 'Dealer Code', alignRight: false },
  { id: 'iccid', label: 'ICCID', alignRight: false },
  { id: 'documentNumber', label: 'Document Number', alignRight: false },
  { id: 'documentType', label: 'Document Type', alignRight: false },
  { id: 'msisdn', label: 'MSISDN', alignRight: false },
  { id: 'organizationName', label: 'Dealer ID', alignRight: false },
  { id: 'transactionType', label: 'Transaction Type', alignRight: false },
  { id: 'organizationType', label: 'Organization Type', alignRight: false },
  { id: 'pin', label: 'Pin', alignRight: false },
  { id: 'posCode', label: 'Pos Code', alignRight: false },
  { id: 'productType', label: 'Product Type', alignRight: false },
  { id: 'recognitionType', label: 'Recognition Type', alignRight: false },
  { id: 'salesDate', label: 'Sales Date', alignRight: false },
  { id: 'sellerName', label: 'Seller Name', alignRight: false },
  { id: 'Contract', label: 'Contract', alignRight: false },
];
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  maxHeight: 500,
  bgcolor: 'background.paper',
  borderRadius: '13px ',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxShadow: 24,
  p: '20px',
};
export default function BasicDatePicker() {
  const [contractLanguage, setContractLanguage] = useState('AZ');
  const [fromDateValue, setFromDateValue] = useState(null);
  const [toDateValue, setToDateValue] = useState(null);
  const [salesValue, setSalesValue] = useState();
  const [userNameValue, setUserNameValue] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [iccid, setIccid] = useState();
  const [transactionDetail, setTransactionDetail] = useState(false);
  const { rowsPerPage, page, handleChangePage, handleChangeRowsPerPage } = stockHooks.useStock();

  const { providerId } = useSelector((state) => ({
    providerId: state.auth.providerId,
  }));

  const params = useMemo(
    () =>
      omitBy(
        {
          providerId,
          dateFrom:
            !isNaN(fromDateValue?.$m) && !isNull(fromDateValue) ? format(fromDateValue?.$d, 'yyyy-MM-dd') : null,
          dateTo: !isNaN(toDateValue?.$m) && !isNull(toDateValue) ? format(toDateValue?.$d, 'yyyy-MM-dd') : null,
          saleType: salesValue,
          dealerCode: userNameValue,
          phoneNumber,
          page: page + 1,
          size: rowsPerPage,
          iccid,
        },
        (v) => v?.toString()?.length < 1
      ),
    [fromDateValue, toDateValue, salesValue, userNameValue, providerId, phoneNumber, page, rowsPerPage, iccid]
  );
  const [trigger, { data, isLoading, isFetching }] = stockApi.useLazyGetDealerArchive(params);
  const [
    getDealerArchiveDetailed,
    { data: detailedTransactionData, isLoading: isLoadingDetailedData, isFetching: isFetchingDetailedData },
  ] = stockApi.useGetDealerArchiveDetailed();

  useEffect(() => {
    trigger(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const handleDownloadContract = async (values) => {
    const { trackId, msisdn } = values;

    const params = {
      trackId,
      referrer: 2,
      language: contractLanguage,
    };

    const response = await axios
      .get('/contract-manager/contract/download', {
        params,
        headers: {
          'Content-Type': 'application/octet-stream',
          ...headers,
        },
        baseURL: BASE_URL,
        responseType: 'blob',
      })
      .catch(() => {
        MySwal.fire({
          title: <p>Error occurred, please contact with support team</p>,
          icon: 'error',
        });
      });
    if (response) {
      FileDownload(response.data, `${msisdn}.pdf`);
    }
  };

  const clearAll = () => {
    setFromDateValue(null);
    setToDateValue(null);
    setSalesValue(null);
    setUserNameValue('');
    setPhoneNumber('');
    setIccid('');
    trigger(params);
  };
  const hadleGetDetailedData = async (msisdn) => {
    try {
      await getDealerArchiveDetailed({ msisdn }).unwrap();
      setTransactionDetail(true);
    } catch (error) {
      // do nothing
    }
  };
  const handleCloseDetailModal = () => {
    setTransactionDetail(false);
  };
  const renderRows = (data) => {
    const rowsToRender = [
      {
        label: 'Name',
        value: data?.name,
      },
      {
        label: 'Surname',
        value: data?.surname,
      },
      {
        label: 'Gender',
        value: data?.gender,
      },
      {
        label: 'Birth Date',
        value: data?.birthDate,
      },
      {
        label: 'Document Number',
        value: data?.documentNumber,
      },
      {
        label: 'PIN',
        value: data?.pin,
      },
      {
        label: 'Document Type',
        value: data?.documentType,
      },
      {
        label: 'Document Expire Date',
        value: data?.documentExpireDate,
      },
      {
        label: 'Address',
        value: data?.address,
      },
      {
        label: 'Number',
        value: data?.number,
      },
      {
        label: 'ICCID',
        value: data?.iccid,
      },
      {
        label: 'Tariff',
        value: data?.tariff,
      },
      {
        label: 'Internet Package',
        value: data?.internetPackage,
      },
      {
        label: 'Seller ID',
        value: data?.sellerId,
      },
      {
        label: 'Seller Phone',
        value: data?.sellerPhone,
      },
      {
        label: 'Number Sold Date',
        value: data?.numberSoldDate,
      },
    ];
    return rowsToRender.map(({ label, value }) => (
      <TableRow hover key={label} tabIndex={-1}>
        <TableCell>{label}</TableCell>
        <TableCell>{value}</TableCell>
      </TableRow>
    ));
  };
  return (
    <Page title="User">
      <Stack direction="row" alignItems="center" mb={5}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid spacing={2} container>
            <Grid item xs={3}>
              {' '}
              <DatePicker
                label="From date"
                value={fromDateValue}
                onChange={(newValue) => {
                  setFromDateValue(newValue);
                }}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </Grid>
            <Grid item xs={3}>
              {' '}
              <DatePicker
                label="To date"
                value={toDateValue}
                onChange={(newValue) => {
                  setToDateValue(newValue);
                }}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel>Sales type:</InputLabel>
                <Select value={salesValue} label="Sales type:" onChange={(e) => setSalesValue(e.target.value)}>
                  <MenuItem value={null}>None</MenuItem>
                  <MenuItem value={'FREE_COMPOUND'}>FREE_COMPOUND</MenuItem>
                  <MenuItem value={'STARTER_PACK'}>STARTER_PACK</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="User name"
                value={userNameValue}
                onChange={(e) => setUserNameValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Iccid" value={iccid} onChange={(e) => setIccid(e.target.value)} />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">994</InputAdornment>,
                }}
                label="Phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                sx={{
                  height: '100%',
                }}
                fullWidth
                variant="contained"
                onClick={() => trigger(params)}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={3}>
              <Button
                sx={{
                  height: '100%',
                }}
                fullWidth
                variant="contained"
                onClick={clearAll}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={3}>
              <FormControl sx={{ px: 1 }}>
                <FormLabel id="demo-radio-buttons-group-label">Contract language</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="female"
                  value={contractLanguage}
                  onChange={(event) => setContractLanguage(event.target.value)}
                  name="radio-buttons-group"
                >
                  <FormControlLabel value="AZ" control={<Radio />} label="AZ" />
                  <FormControlLabel value="EN" control={<Radio />} label="EN" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Stack>
      <Card>
        {(isLoading || isFetching || isLoadingDetailedData || isFetchingDetailedData) && <LinearProgress />}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 1000 }}>
            <Table>
              <UserListHead headLabel={TABLE_HEAD} rowCount={data?.data?.length} />
              <TableBody>
                {data?.data?.map((row, index) => {
                  const {
                    dealerCode,
                    documentNumber,
                    documentType = '',
                    msisdn,
                    organizationName,
                    organizationType = '',
                    transactionType,
                    recognitionType,
                    pin,
                    posCode,
                    productType = '',
                    salesDate,
                    sellerName,
                    iccid,
                    pinForContract,
                  } = row;

                  return (
                    <TableRow hover key={index} tabIndex={-1}>
                      <TableCell align="left">{index + 1}</TableCell>
                      <TableCell align="center">
                        <Button onClick={() => hadleGetDetailedData(msisdn)}>
                          <InfoOutlinedIcon />
                        </Button>
                      </TableCell>
                      <TableCell>{dealerCode}</TableCell>
                      <TableCell>{iccid}</TableCell>
                      <TableCell>{documentNumber}</TableCell>
                      <TableCell>{sentenceCase(documentType || '')}</TableCell>
                      <TableCell>{msisdn}</TableCell>
                      <TableCell>{organizationName}</TableCell>
                      <TableCell>{sentenceCase(transactionType || '')}</TableCell>
                      <TableCell>{sentenceCase(organizationType || '')}</TableCell>
                      <TableCell>{pin}</TableCell>
                      <TableCell>{posCode}</TableCell>
                      <TableCell>{sentenceCase(productType || '')}</TableCell>
                      <TableCell>{sentenceCase(recognitionType || '')}</TableCell>
                      <TableCell>{format(new Date(salesDate), 'yyyy_MM_dd')}</TableCell>
                      <TableCell>{sellerName}</TableCell>
                      <TableCell align="left">
                        <Button
                          sx={{
                            marginLeft: '-1rem',
                          }}
                          variant="contained"
                          onClick={() => handleDownloadContract(row, pinForContract)}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={get(data, 'metadata.pagination.total', 0)}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      {transactionDetail && (
        <Modal
          open={transactionDetail}
          onClose={handleCloseDetailModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography align="center" id="modal-modal-title" variant="h6" component="h2">
              Transaction Detail
            </Typography>
            <TableContainer>
              <Table>{detailedTransactionData && renderRows(detailedTransactionData)}</Table>
            </TableContainer>

            <Button onClick={handleCloseDetailModal} sx={{ mt: '5px' }} variant="contained">
              Close
            </Button>
          </Box>
        </Modal>
      )}
    </Page>
  );
}
